import {Skeleton} from 'antd';
import {useForumDataFetcher} from 'course/forum/firebase/ForumDataFetcher';
import {getDocFromServer} from 'firebase/firestore';
import {useCourseId} from 'hooks/router/useUrlParams';
import {useContext} from 'react';
import {useQuery} from '@tanstack/react-query';

import {ProfileContext} from 'contexts/ProfileContext';
import Gate from 'contexts/Gate';
import {firebaseStringToRole, roleToForumString} from 'contexts/profileUtil';
import styled from 'styled-components';
import {FaGhost} from 'react-icons/fa';
import {isFirebaseRole, RoleFirebaseString} from 'types/role';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {Link} from 'react-router-dom';
import {isNullOrUndefined} from 'utils/general';

export function UserChip(props: {
  showAvatar: boolean;
  uid: string;
  name: string;
  disableLink?: boolean;
}) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const {userData, loading: userIsLoading} = useContext(ProfileContext);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const courseId = useCourseId();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const uid = props.uid;

  if (Gate.hasStaffRole(userData)) {
    return (
      <UserChipButton>
        <UserChipContents {...props} />
      </UserChipButton>
    );
  }
  return <UserChipContents {...props} />;
}

export function UserChipContents(props: {
  showAvatar: boolean;
  uid: string;
  name: string;
  disableLink?: boolean;
}) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const {uid, name, showAvatar, disableLink} = props;
  const dataFetcher = useForumDataFetcher();
  const t = useTranslateFunction('forum');

  const courseId = useCourseId();

  const userQuery = useQuery({
    queryKey: ['userData', uid],
    queryFn: () => getDocFromServer(dataFetcher.get_user_data_ref(uid)),
  });

  const userRoleQuery = useQuery({
    queryKey: ['userRole', uid],
    queryFn: () =>
      getDocFromServer(dataFetcher.get_user_role_ref(uid, courseId)),
  });

  if (userQuery.isPending || userRoleQuery.isPending) {
    return (
      <UserDetailsDisabled>
        <Skeleton paragraph={false} />
      </UserDetailsDisabled>
    );
  }

  // No user doc means a deleted account: the deletion cascade removes
  // `users/{uid}` and sets the name stored on the post to
  // DELETED_USER_DISPLAY_NAME (see the @cip/core/accountDeletion README).
  // Show that stored name, with no profile link, rather than inventing one.
  if (userQuery.data?.exists() === false) {
    return (
      <UserDetailsDisabled>
        {showAvatar && (
          <div style={userIconStyle}>
            <FaGhost aria-hidden="true" />
          </div>
        )}
        <DisplayName style={{fontWeight: 'normal'}}>
          {isNullOrUndefined(name) || name === ''
            ? t('Anonymous', {forceString: true})
            : name}
        </DisplayName>
      </UserDetailsDisabled>
    );
  }

  const userData = userQuery?.data?.data() ?? {
    displayName: 'A',
  };

  let courseRole = null;
  if (userRoleQuery.data) {
    const roleData = userRoleQuery.data.data();
    courseRole = roleData?.role;
  }

  const displayName =
    userData?.displayName ?? t('Anonymous', {forceString: true});

  const role = getRole(courseRole) as RoleFirebaseString;

  const safePhotoURL = userData['photoURL'] ?? '';

  if (disableLink) {
    return (
      <UserDetailsDisabled>
        {showAvatar && <UserIcon photoURL={safePhotoURL} name={displayName} />}
        <DisplayName style={{fontWeight: 'normal'}}>{displayName}</DisplayName>
        <RoleBadge role={role} />
      </UserDetailsDisabled>
    );
  }
  return (
    <UserDetails to={`/${courseId}/user/${uid}`}>
      {showAvatar && <UserIcon photoURL={safePhotoURL} name={displayName} />}
      <DisplayName style={{fontWeight: 'normal'}}>{displayName}</DisplayName>
      <RoleBadge role={role} />
    </UserDetails>
  );
}

export const RoleBadge = (props: {role: RoleFirebaseString}) => {
  const {role} = props;
  const t = useTranslateFunction('forum');

  if (role === 'student' || role === 'experiencedstudent') {
    return <></>;
  }

  const roleLabel = roleToForumString(firebaseStringToRole(role));

  return (
    <span
      className="badge"
      style={{
        color: 'white',
        backgroundColor: '#7863D4',
      }}
    >
      {t(roleLabel)}
    </span>
  );
};

export const UserIcon = (props: {photoURL: string; name: string}) => {
  // draw a salmon circle with the letter C in the middle
  let initial = '';
  if (props.name) {
    if (props.name.length > 0) {
      initial = props.name[0];
    }
  }

  if (props?.photoURL) {
    return <ProfilePhoto src={props.photoURL} crossOrigin="anonymous" />;
  }

  return <div style={userIconStyle}>{initial}</div>;
};

export const GhostUserChip = (props: {showAvatar: boolean}) => {
  const {showAvatar} = props;
  const t = useTranslateFunction('forum');
  return (
    <UserDetails to={``}>
      {showAvatar && (
        <div style={userIconStyle}>
          <FaGhost aria-hidden="true" />
        </div>
      )}
      <DisplayName style={{fontWeight: 'normal'}}>
        {t('Anonymous Coder')}
      </DisplayName>
    </UserDetails>
  );
};

const getRole = (courseRole: string): RoleFirebaseString => {
  if (!isFirebaseRole(courseRole)) {
    return 'student';
  }
  return courseRole;
};

const userIconStyle = {
  width: '40px',
  height: '40px',
  color: 'white',
  borderRadius: '50%',
  backgroundColor: 'salmon',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '24px',
};

const UserChipButton = styled.div`
  padding: 0px;
`;

const UserDetails = styled(Link)`
  display: flex;
  gap: 5px;
  align-items: center;
  overflow: visible;
`;

const UserDetailsDisabled = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  overflow: visible;
  pointer-events: none;
`;

const DisplayName = styled.div`
  position: relative;
  left: -3px;
  color: var(--forum-text, black);
  padding-left: 2px;
  padding-right: 2px;
  margin: 1px;
  overflow: visible;
  &:focus-visible {
    color: var(--forum-text, black);
    outline-offset: -3px;
    outline: 2px solid #316cf4;
  }
`;

const ProfilePhoto = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
`;
